import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import TeamProfileCardTrigger from '@atlaskit/profilecard/team';
import { TeamProfileCardWithTrigger } from '@atlassian/team-profilecard/trigger';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { messages } from '../../messages';
import { useMeaningfulHover } from '../../v2/common/hooks/use-meaningful-hover';
import { KnowledgeCardsRowRefresh } from '../knowledge-cards-row';
import { KnowledgeCardsRowType } from '../types';
import { createProfileClient, getTeamProfileUrl } from '../util';

type TeamMember = {
	accountStatus: string;
	name: string;
	picture: string;
	id: string;
};

type TeamData = {
	team: {
		id: string;
		organizationId: string;
		membershipSettings: string;
		description: string;
		displayName: string;
		state: string;
		largeAvatarImageUrl: string;
		smallAvatarImageUrl: string;
		largeHeaderImageUrl: string;
		smallHeaderImageUrl: string;
		isVerified: boolean;
		members: {
			nodes: Array<{
				member: TeamMember;
			}>;
		};
	};
	memberCount: number;
	includesYou: boolean;
};

type KnowledgeCardTeamsRowProps = {
	item: TeamData;
	maxItems: number;
	onClickCallback?: (isAuxClick?: boolean) => void;
};

export const KnowledgeCardTeamsRow: React.MemoExoticComponent<
	({ item, onClickCallback }: KnowledgeCardTeamsRowProps) => React.JSX.Element
> = React.memo(
	({ item: teamData, onClickCallback }: KnowledgeCardTeamsRowProps): React.JSX.Element => {
		const { commonAttributes, userDetails } = useAIAnswerContext();
		const { cloudId, orgId } = commonAttributes;
		const { formatMessage } = useIntl();

		const team = teamData.team;

		const teamUrl = getTeamProfileUrl(team.id.split('/').pop() || team.id, orgId);

		const handleTeamClick = (e: React.MouseEvent) => {
			const isAuxClick = 'button' in e && e.button === 1;
			if (isAuxClick) {
				window.open(teamUrl, '_blank', 'noopener,noreferrer');
			} else {
				window.open(teamUrl, '_self', 'noopener, noreferrer');
			}
		};

		const profileClient = useMemo(() => createProfileClient(cloudId), [cloudId]);

		const { handleHoverStart, handleHoverLeave } = useMeaningfulHover({
			elementType: 'team',
			hoverCardSelector: '[data-testid="team-profilecard"]',
		});

		if (fg('ptc-enable-team-profilecard-package')) {
			return (
				<TeamProfileCardWithTrigger
					trigger="hover"
					teamAri={team.id}
					cloudId={commonAttributes.cloudId}
					placement="bottom-start"
					userId={userDetails.id || ''}
				>
					<Box onMouseEnter={handleHoverStart} onMouseLeave={handleHoverLeave}>
						<KnowledgeCardsRowRefresh
							primaryText={team.displayName}
							secondaryText={formatMessage(messages.knowledge_cards_team_member_count, {
								count: teamData.memberCount,
							})}
							pictureUrl={team.smallAvatarImageUrl}
							type={KnowledgeCardsRowType.AGENT}
							onClick={(e: React.MouseEvent<HTMLDivElement>) => {
								onClickCallback?.();
								handleTeamClick(e);
							}}
							onAuxClick={(e: React.MouseEvent<HTMLDivElement>) => {
								onClickCallback?.(true);
								handleTeamClick(e);
							}}
						/>
					</Box>
				</TeamProfileCardWithTrigger>
			);
		}

		if (!profileClient) {
			return (
				<KnowledgeCardsRowRefresh
					primaryText={team.displayName}
					secondaryText={formatMessage(messages.knowledge_cards_team_member_count, {
						count: teamData.memberCount,
					})}
					pictureUrl={team.smallAvatarImageUrl}
					type={KnowledgeCardsRowType.AGENT}
					onClick={(e: React.MouseEvent<HTMLDivElement>) => {
						onClickCallback?.();
						handleTeamClick(e);
					}}
					onAuxClick={(e: React.MouseEvent<HTMLDivElement>) => {
						onClickCallback?.(true);
						handleTeamClick(e);
					}}
				/>
			);
		}

		return (
			<TeamProfileCardTrigger
				trigger="hover"
				teamId={team.id}
				cloudId={commonAttributes.cloudId}
				position="bottom-start"
				viewProfileLink={teamUrl}
				resourceClient={profileClient}
			>
				<Box onMouseEnter={handleHoverStart} onMouseLeave={handleHoverLeave}>
					<KnowledgeCardsRowRefresh
						primaryText={team.displayName}
						secondaryText={formatMessage(messages.knowledge_cards_team_member_count, {
							count: teamData.memberCount,
						})}
						pictureUrl={team.smallAvatarImageUrl}
						type={KnowledgeCardsRowType.AGENT}
						onClick={(e: React.MouseEvent<HTMLDivElement>) => {
							onClickCallback?.();
							handleTeamClick(e);
						}}
						onAuxClick={(e: React.MouseEvent<HTMLDivElement>) => {
							onClickCallback?.(true);
							handleTeamClick(e);
						}}
					/>
				</Box>
			</TeamProfileCardTrigger>
		);
	},
);
